import { Children, cloneElement, Component, isValidElement } from 'react';
import { Status } from '@optimus-web/common';
import { FormLabel } from '../common';
import { FormMessage } from '../common/FormMessage';
import { isComponentOfType } from '../utils/isComponentOfType';
import { extractAriaProps } from '../utils/extractAriaProps';
import { RadioButton } from './RadioButton';
import { RadioButtonGroupContainer, RadioGroupElement } from './style';

export interface RadioGroupProps {
    className?: string;
    disabled?: boolean;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    value?: boolean | string | number;
    label?: React.ReactNode;
    error?: React.ReactNode;
    required?: boolean;
    id?: string;
    name?: string;
    as?: React.ElementType;
}

type ChildProps = RadioGroupProps & {
    checked?: boolean;
    'data-test-radio'?: RadioGroupProps['value'];
    'aria-describedby'?: string;
};

export const RadioGroup = class RadioGroupComponent extends Component<React.PropsWithChildren<RadioGroupProps>> {
    render() {
        const { className, disabled, children, value, label, error, required, id, name, as, ...other } = this.props;

        const [ariaProps] = extractAriaProps(other);

        // Generate unique IDs for accessibility if not provided
        const groupId = id || `radio-group-${Math.random().toString(36).substr(2, 9)}`;
        const groupName = name || `radio-group-${groupId}`;
        const labelId = `${groupId}-label`;
        const errorId = error ? `${groupId}-error` : undefined;

        return (
            <RadioGroupElement
                className={className}
                role="radiogroup"
                aria-labelledby={labelId}
                aria-describedby={errorId}
                as={as}
                {...ariaProps}
            >
                {label ? (
                    <FormLabel required={required} as="legend" id={labelId}>
                        {label}
                    </FormLabel>
                ) : null}
                <RadioButtonGroupContainer>
                    {Children.map(children, (child) =>
                        isValidElement<ChildProps>(child) && isComponentOfTypeRadioButton(child)
                            ? cloneElement(child, {
                                  ...other,
                                  checked: child.props.value === value,
                                  disabled: disabled || child.props.disabled,
                                  'data-test-radio': child.props.value,
                                  name: groupName,
                                  'aria-describedby': errorId,
                              })
                            : child
                    )}
                </RadioButtonGroupContainer>
                {error ? (
                    <FormMessage status={Status.Error} id={errorId}>
                        {error}
                    </FormMessage>
                ) : null}
            </RadioGroupElement>
        );
    }
};

const isComponentOfTypeRadioButton = isComponentOfType(RadioButton);
